import { useEffect, useState } from "react";
import { api, subRoleLabel } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Copy, Trophy } from "lucide-react";
import { toast } from "sonner";

export default function TeamView() {
  const { user } = useAuth();
  const [team, setTeam] = useState(null);
  const [board, setBoard] = useState([]);

  useEffect(() => {
    (async () => {
      const t = await api.get("/team/mine");
      setTeam(t.data);
      const b = await api.get("/team/leaderboard");
      setBoard(b.data);
    })();
  }, []);

  if (!user?.team_id) {
    return (
      <div className="p-10">
        <div className="hairline bg-[#0F0F0F] p-10 max-w-2xl">
          <h1 className="font-display text-3xl font-black mb-2">No team yet</h1>
          <p className="text-neutral-400 text-sm mb-4">
            Head to <span className="text-[#39FF14]">Profile</span> to create a team (as leader) or join with a code.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 md:p-10 max-w-[1400px]">
      <div className="mb-8">
        <div className="text-[10px] uppercase tracking-widest text-[#39FF14] mb-2">// Team</div>
        <h1 className="font-display text-4xl md:text-5xl font-black tracking-tight">{team?.team?.name}</h1>
        {team?.team?.join_code && user.role === "team_leader" && (
          <div className="mt-4 flex items-center gap-3">
            <span className="text-xs text-neutral-500 uppercase tracking-widest">Join code</span>
            <button
              data-testid="copy-team-code-btn"
              onClick={() => {
                navigator.clipboard.writeText(team.team.join_code);
                toast.success("Team code copied");
              }}
              className="hairline px-3 py-1.5 font-mono text-sm text-[#39FF14] hover:bg-[#111] flex items-center gap-2"
            >
              {team.team.join_code} <Copy size={12} />
            </button>
          </div>
        )}
      </div>

      <div className="hairline bg-[#0F0F0F]">
        <div className="flex items-center gap-2 p-5 border-b border-neutral-900">
          <Trophy size={16} className="text-[#39FF14]" />
          <h2 className="font-display text-sm font-bold uppercase tracking-widest">Week Leaderboard</h2>
        </div>
        <div className="divide-y divide-neutral-900">
          <div className="grid grid-cols-12 px-5 py-3 text-[10px] uppercase tracking-widest text-neutral-600">
            <div className="col-span-1">#</div>
            <div className="col-span-4">Member</div>
            <div className="col-span-1 text-right">Calls</div>
            <div className="col-span-1 text-right">Leads</div>
            <div className="col-span-1 text-right">Appts</div>
            <div className="col-span-1 text-right">Offers</div>
            <div className="col-span-1 text-right">Contracts</div>
            <div className="col-span-2 text-right">Revenue</div>
          </div>
          {board.length === 0 && (
            <div className="p-8 text-center text-neutral-500 text-sm">No members yet — share your join code.</div>
          )}
          {board.map((m, i) => (
            <div
              key={m.user_id}
              data-testid={`leaderboard-row-${i}`}
              className="grid grid-cols-12 items-center px-5 py-4 hover:bg-[#111] transition-colors"
            >
              <div className="col-span-1 font-display text-lg font-bold text-neutral-500 tabular">{i + 1}</div>
              <div className="col-span-4 flex items-center gap-3">
                {m.picture ? (
                  <img src={m.picture} className="w-9 h-9 hairline object-cover" alt="" />
                ) : (
                  <div className="w-9 h-9 bg-[#1a1a1a] hairline flex items-center justify-center text-xs font-bold">
                    {m.name[0]}
                  </div>
                )}
                <div className="min-w-0">
                  <div className="font-semibold text-sm truncate">{m.name}</div>
                  <div className="text-[10px] uppercase tracking-widest text-neutral-500">
                    {subRoleLabel(m.sub_role)}
                  </div>
                </div>
              </div>
              <div className="col-span-1 text-right tabular text-sm">{m.week_totals.calls}</div>
              <div className="col-span-1 text-right tabular text-sm">{m.week_totals.leads}</div>
              <div className="col-span-1 text-right tabular text-sm">{m.week_totals.appointments}</div>
              <div className="col-span-1 text-right tabular text-sm">{m.week_totals.offers}</div>
              <div className="col-span-1 text-right tabular text-sm font-bold">{m.week_totals.contracts}</div>
              <div className="col-span-2 text-right tabular text-sm font-display font-bold text-[#39FF14]">
                ${Number(m.week_totals.revenue).toLocaleString()}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
